// Batch email scope — the pure core shared by BOTH render engines.
//
// `shared/services/email/EmailService.ts` (browser) and
// `supabase/functions/google-api-service/template-pipeline.ts` (Deno edge fn)
// are explicitly mirrored engines and drift between them is a known hazard.
// Rather than copy the batch logic into both, everything that can be pure lives
// HERE and both engines import it — the mirror is then structural, not a
// convention someone has to remember.
//
// That includes the three per-transaction resolution rules (recipients,
// attachments, language) which were previously inline inside each engine's
// render function. Both engines now CALL these, so a single-transaction email
// and one transaction inside a batch resolve identically by construction.
//
// Nothing here touches Supabase, Handlebars, or any runtime global.

import {
  filterFiles, getContactEmails, selectApprovedFile,
  type CompanyLike, type FileLike,
} from './helpers.ts';

// ── Config surface ──────────────────────────────────────────────────────

export type BatchRecipientsPolicy = 'union' | 'split';
export type BatchLanguagePolicy = 'block' | 'majority' | 'first';

/** The subset of `template_configs` the pure batch functions read. */
export interface BatchCapableConfig {
  email_language_source?: string | null;
  email_recipients_config?: { company: string; purpose: string } | null;
  email_recipients_static?: string[] | null;
  email_attachments_config?: Array<{ document_type: string; select: 'filter' | 'approved' | 'generated' }> | null;
  scope?: string | null;
  email_batch_recipients_policy?: string | null;
  email_batch_language_policy?: string | null;
}

/** Upper bound on a single batch selection — guards edge-fn memory and Gmail. */
export const MAX_BATCH_TRANSACTIONS = 50;

/** Validation issue key raised when the language policy is 'block' and languages differ. */
export const BATCH_LANGUAGE_CONFLICT = 'batch_language_conflict';

export function batchRecipientsPolicy(config: BatchCapableConfig): BatchRecipientsPolicy {
  return config.email_batch_recipients_policy === 'union' ? 'union' : 'split';
}

export function batchLanguagePolicy(config: BatchCapableConfig): BatchLanguagePolicy {
  const raw = config.email_batch_language_policy;
  return raw === 'majority' || raw === 'first' ? raw : 'block';
}

export function isBatchConfig(config: BatchCapableConfig): boolean {
  return config.scope === 'batch';
}

// ── Per-transaction resolution (shared with the single-transaction path) ──

type Scope = Record<string, unknown>;

/**
 * Recipients for ONE transaction scope.
 * Precedence: static (if non-empty) → config-driven contacts → resolver override.
 * Lifted verbatim out of EmailService.renderEmail / prepareEmailData so a
 * transaction resolves the same addresses whether it is sent alone or in a batch.
 */
export function resolveRecipientsFor(config: BatchCapableConfig, data: Scope): string[] {
  if (config.email_recipients_static?.length) return config.email_recipients_static;
  if (config.email_recipients_config) {
    const company = data[config.email_recipients_config.company] as CompanyLike | undefined;
    if (company) return getContactEmails(company, config.email_recipients_config.purpose);
    return [];
  }
  if (data.emailRecipients) return data.emailRecipients as string[];
  return [];
}

/**
 * Attachments for ONE transaction scope, per `email_attachments_config`.
 * Precedence: config-driven selection → resolver override. Lifted verbatim out
 * of both engines' render functions.
 */
export function selectAttachmentsFor(config: BatchCapableConfig, data: Scope): FileLike[] {
  const files = data.files as FileLike[] | undefined;
  if (config.email_attachments_config?.length && files) {
    const attachments: FileLike[] = [];
    for (const att of config.email_attachments_config) {
      if (att.select === 'approved') {
        const f = selectApprovedFile(files, att.document_type);
        if (f) attachments.push(f);
      } else if (att.select === 'generated') {
        const f = files.find(
          (file) => file.isLatest && file.documentType === att.document_type && file.documentStatus === 'generated',
        );
        if (f) attachments.push(f);
      } else {
        attachments.push(...filterFiles(files, [att.document_type]));
      }
    }
    return attachments;
  }
  if (data.emailAttachments) return data.emailAttachments as FileLike[];
  return [];
}

/**
 * Language for ONE transaction scope.
 * Precedence: resolver override (`emailLanguage`) → the company named by
 * `email_language_source` → 'en' when that source is configured but carries no
 * language → 'pl'. Mirrors the language ladder inside both render functions.
 */
export function languageFor(config: BatchCapableConfig, data: Scope): string {
  const override = data.emailLanguage;
  if (typeof override === 'string' && override) return override;
  if (config.email_language_source) {
    const companyObj = data[config.email_language_source] as Record<string, unknown> | undefined;
    return (companyObj?.languageCode as string) || 'en';
  }
  return 'pl';
}

// ── Language conflict policy ────────────────────────────────────────────

export interface BatchLanguageDecision {
  /** The variant the batch renders in. Always defined so the operator sees a preview. */
  language: string;
  /** true when the selection carried more than one distinct language. */
  conflict: boolean;
  /** true when the conflict must block the send (policy 'block'). */
  blocked: boolean;
  /** Distinct languages seen, in first-seen order. */
  languages: string[];
}

/**
 * Apply the configured conflict policy to the per-transaction languages.
 * 'block' still returns a renderable language (the majority) so the modal can
 * show a preview — it just also reports `blocked`, which the caller turns into
 * a critical validation issue.
 */
export function pickBatchLanguage(langs: string[], policy: BatchLanguagePolicy): BatchLanguageDecision {
  const distinct: string[] = [];
  const counts = new Map<string, number>();
  for (const lang of langs) {
    if (!counts.has(lang)) distinct.push(lang);
    counts.set(lang, (counts.get(lang) ?? 0) + 1);
  }

  if (distinct.length === 0) {
    return { language: 'pl', conflict: false, blocked: false, languages: [] };
  }

  const conflict = distinct.length > 1;
  const first = distinct[0];

  // Majority with a first-seen tie-break — Map preserves insertion order, so
  // scanning `distinct` (not the Map) keeps the winner deterministic.
  let majority = first;
  let best = counts.get(first) ?? 0;
  for (const lang of distinct) {
    const n = counts.get(lang) ?? 0;
    if (n > best) { majority = lang; best = n; }
  }

  if (policy === 'first') {
    return { language: first, conflict, blocked: false, languages: distinct };
  }
  if (policy === 'majority') {
    return { language: majority, conflict, blocked: false, languages: distinct };
  }
  return { language: majority, conflict, blocked: conflict, languages: distinct };
}

// ── Recipient grouping policy ───────────────────────────────────────────

export interface BatchTxEntry<T = Scope> {
  transactionId: number;
  referenceNumber: string;
  scope: T;
  recipients: string[];
  language: string;
  attachments: FileLike[];
}

export interface BatchRecipientGroup<T = Scope> {
  /** De-duped recipients for this outgoing email, in first-seen order. */
  recipients: string[];
  entries: BatchTxEntry<T>[];
}

/**
 * De-dupe an address list case-insensitively while preserving the casing and
 * order of the first occurrence — operators paste mixed-case addresses and
 * `A@x.pl` / `a@x.pl` are the same mailbox.
 */
export function dedupeRecipients(recipients: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of recipients) {
    const address = (raw ?? '').trim();
    if (!address) continue;
    const key = address.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(address);
  }
  return out;
}

/** Stable identity of a recipient SET — order-independent, case-insensitive. */
function recipientSetKey(recipients: string[]): string {
  return dedupeRecipients(recipients).map((r) => r.toLowerCase()).sort().join('|');
}

/**
 * Split the selection into outgoing emails per the configured policy.
 *
 * - 'union' → exactly one group addressed to the de-duped union of every
 *   transaction's recipients.
 * - 'split' → one group per distinct recipient set, in first-seen order.
 *   Transactions that resolved NO recipients share the empty-set group, which
 *   the caller turns into a `recipients` critical issue.
 */
export function groupTransactionsByRecipients<T = Scope>(
  entries: BatchTxEntry<T>[],
  policy: BatchRecipientsPolicy,
): BatchRecipientGroup<T>[] {
  if (entries.length === 0) return [];

  if (policy === 'union') {
    return [{
      recipients: dedupeRecipients(entries.flatMap((e) => e.recipients)),
      entries: [...entries],
    }];
  }

  const groups: BatchRecipientGroup<T>[] = [];
  const index = new Map<string, BatchRecipientGroup<T>>();
  for (const entry of entries) {
    const key = recipientSetKey(entry.recipients);
    let group = index.get(key);
    if (!group) {
      group = { recipients: dedupeRecipients(entry.recipients), entries: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.entries.push(entry);
  }
  return groups;
}

// ── Attachment aggregation ──────────────────────────────────────────────

export interface BatchAttachmentGrouping {
  /** De-duped, flattened attachment list — what actually rides the MIME message. */
  flat: FileLike[];
  /** Per-source-transaction view, for the modal. Only the files that survived de-dupe. */
  groups: Array<{ transaction_id: number; reference_number: string; files: FileLike[] }>;
}

/**
 * Aggregate per-transaction attachment selections into one send list.
 * De-dupes by file id across the whole group — the same
 * `transaction_file_id` attached from two transactions rides the message once,
 * and is displayed under the FIRST transaction that contributed it.
 */
export function dedupeAttachments(
  per: Array<{ transactionId: number; referenceNumber: string; files: FileLike[] }>,
): BatchAttachmentGrouping {
  const seen = new Set<unknown>();
  const flat: FileLike[] = [];
  const groups: BatchAttachmentGrouping['groups'] = [];

  for (const { transactionId, referenceNumber, files } of per) {
    const kept: FileLike[] = [];
    for (const file of files) {
      // Files without an id can't be de-duped; keep them (resolver-supplied
      // overrides are the only realistic source and they're already explicit).
      const id = (file as { id?: unknown }).id;
      if (id != null) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      kept.push(file);
      flat.push(file);
    }
    groups.push({ transaction_id: transactionId, reference_number: referenceNumber, files: kept });
  }

  return { flat, groups };
}

/**
 * Disambiguate an attachment filename inside a batch.
 * Ten transactions each contributing `CMR.pdf` would arrive as ten identically
 * named parts; prefixing with the reference number keeps them distinguishable
 * in the recipient's mail client. Applied unconditionally in batch mode —
 * deterministic beats collision-detected.
 */
export function batchAttachmentFilename(referenceNumber: string | null | undefined, fileName: string): string {
  const ref = (referenceNumber ?? '').trim().replace(/[\\/:*?"<>|]+/g, '-');
  if (!ref) return fileName;
  if (fileName.startsWith(`${ref}_`)) return fileName;
  return `${ref}_${fileName}`;
}

// ── Batch scope assembly ────────────────────────────────────────────────

export interface AssembleBatchScopeParams<T = Scope> {
  /** Non-transaction data sources (currentUser, dictionaries) — fetched ONCE. */
  staticScope: Scope;
  /** The per-transaction resolved scopes belonging to THIS outgoing email. */
  entries: BatchTxEntry<T>[];
  /** Language chosen by the conflict policy. */
  language: string;
  /** `formatDate(new Date())` — supplied by the caller so this stays pure. */
  currentDate: string;
}

/**
 * Build the Handlebars scope for one outgoing batch email.
 *
 * `isBatchScope` is the discriminator that lets ONE `resolvers` string serve
 * both passes: it is `true` here and `false` on the per-transaction pass, and
 * is never present at all in a single-transaction `prepare()` scope — so
 * existing single templates are untouched.
 */
export function assembleBatchScope<T = Scope>(params: AssembleBatchScopeParams<T>): Scope {
  const { staticScope, entries, language, currentDate } = params;
  return {
    ...staticScope,
    transactions: entries.map((e) => e.scope),
    referenceNumbers: entries.map((e) => e.referenceNumber),
    count: entries.length,
    isBatchScope: true,
    emailLanguage: language,
    currentDate,
  };
}

/**
 * The names `assembleBatchScope` injects — they exist ONLY at the batch level.
 * Keep in lockstep with the object above.
 */
export const BATCH_SCOPE_KEYS: readonly string[] = [
  'transactions', 'referenceNumbers', 'count', 'isBatchScope', 'emailLanguage', 'currentDate',
];

/**
 * True when a placeholder path is rooted at a batch-level name.
 *
 * The placeholder sweep runs PER TRANSACTION (loop-scoped paths like
 * `referenceNumber` only resolve there), but a batch body legitimately also
 * references `{{count}}` and `{{#each transactions}}` — names that by definition
 * are absent from a single transaction's scope. Without this filter every batch
 * send would report those as missing on every transaction, forcing an admin to
 * classify them as optional purely to unblock the send. They need no validation:
 * the batch render itself is what proves they resolved.
 */
export function isBatchScopePath(path: string): boolean {
  const root = path.split(/[.[]/)[0];
  return BATCH_SCOPE_KEYS.includes(root);
}
